import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { queryClient } from "@/lib/queryClient";
import App from "./App.tsx";
import "./index.css";
import { setupErrorTracking } from "./lib/errorTracking";
import { trackWebVitals } from "./lib/analytics";

// Setup error tracking
setupErrorTracking();

// Track Web Vitals
trackWebVitals();

// Check if we're in Lovable preview/editor (avoid SW interference there)
// Covers: *.lovable.app, *.lovableproject.com, id-preview--* subdomains
const isLovablePreview = 
  location.hostname.endsWith('.lovable.app') ||
  location.hostname.endsWith('.lovableproject.com') ||
  location.hostname.startsWith('id-preview--');

// In preview: unregister any existing SW and clear caches to prevent stale bundle issues
if (isLovablePreview && 'serviceWorker' in navigator) {
  // Unregister all service workers
  navigator.serviceWorker.getRegistrations().then(registrations => {
    registrations.forEach(registration => {
      registration.unregister().then(() => {
        console.log('[Preview] Service Worker unregistered');
      });
    });
  });
  
  // Clear all caches
  if ('caches' in window) {
    caches.keys().then(names => {
      names.forEach(name => {
        caches.delete(name).then(() => {
          console.log('[Preview] Cache deleted:', name);
        });
      });
    });
  }
}

// Register service worker for PWA capabilities + auto-update on new build (only on live domain)
// Deferred using requestIdleCallback or load event to not block TTI
if ('serviceWorker' in navigator && import.meta.env.PROD && !isLovablePreview) {
  const registerServiceWorker = () => {
    navigator.serviceWorker.register('/service-worker.js')
      .then(registration => {
        console.log('Service Worker registered:', registration);

        // Auto-activate updated SW
        const listenForWaiting = (reg: ServiceWorkerRegistration) => {
          if (reg.waiting) {
            reg.waiting.postMessage({ type: 'SKIP_WAITING' });
          }
        };

        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (!newWorker) return;
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed') listenForWaiting(registration);
          });
        });

        // Reload to take control (only in production)
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          if (import.meta.env.PROD) {
            window.location.reload();
          }
        });
      })
      .catch(error => {
        console.log('Service Worker registration failed:', error);
      });
  };
  
  // Schedule registration during idle time or after load
  if (typeof (window as any).requestIdleCallback === 'function') {
    (window as any).requestIdleCallback(registerServiceWorker);
  } else {
    window.setTimeout(() => {
      registerServiceWorker();
    }, 2000);
  }
}

// Defer React rendering to reduce main-thread blocking during initial load
// Static hero content provides instant visual feedback while React initializes
const initializeApp = () => {
  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <HelmetProvider>
        <App />
      </HelmetProvider>
    </StrictMode>
  );

  // Signal to index.html loader that the app has mounted
  try {
    window.dispatchEvent(new Event('app:mounted'));
  } catch {}
};

// Immediate initialization since we're already deferring the script load in index.html
initializeApp();

